import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Users as UsersIcon, Mail, Trash2, KeyRound, Search } from "lucide-react";
import { initials } from "@/lib/helpers";

const roleBadge = {
  admin: "bg-primary/10 text-primary border-primary/20",
  trainer: "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400 border-violet-200 dark:border-violet-500/30",
  employee: "bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300 border-slate-200 dark:border-slate-500/30",
};

export default function Users() {
  const [users, setUsers] = useState(null);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => { const { data } = await api.get("/users"); setUsers(data); };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("User removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!users) return <Loading />;
  const s = q.trim().toLowerCase();
  const filtered = (tab === "all" ? users : users.filter((u) => u.role === tab))
    .filter((u) => !s || (u.name||"").toLowerCase().includes(s) || (u.email||"").toLowerCase().includes(s) || (u.department||"").toLowerCase().includes(s));

  return (
    <div>
      <PageHeader title="People & Roles" subtitle="Manage learners, trainers and administrators." testid="users-header">
        <Button onClick={() => setOpen(true)} data-testid="add-user-button"><Plus className="h-4 w-4 mr-1.5" />Add Person</Button>
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab} className="mb-5">
        <TabsList>
          <TabsTrigger value="all" data-testid="users-tab-all">All ({users.length})</TabsTrigger>
          <TabsTrigger value="admin" data-testid="users-tab-admin">Admins</TabsTrigger>
          <TabsTrigger value="trainer" data-testid="users-tab-trainer">Trainers</TabsTrigger>
          <TabsTrigger value="employee" data-testid="users-tab-employee">Learners</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="relative max-w-sm mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or department..." className="pl-9" data-testid="search-users-input" />
      </div>

      {filtered.length === 0 ? <EmptyState message="No people in this group." icon={UsersIcon} /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((u) => (
            <Card key={u.id} className="p-5 stagger-in" data-testid={`user-card-${u.id}`}>
              <div className="flex items-start gap-3">
                <Avatar className="h-12 w-12"><AvatarImage src={u.avatar} /><AvatarFallback className="bg-primary/10 text-primary">{initials(u.name)}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <p className="font-heading font-semibold truncate">{u.name}</p>
                  <p className="text-xs text-muted-foreground truncate flex items-center gap-1"><Mail className="h-3 w-3" />{u.email}</p>
                  <Badge variant="outline" className={`mt-2 capitalize border ${roleBadge[u.role]}`}>{u.role === "employee" ? "learner" : u.role}</Badge>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 gap-2 text-xs">
                <div><p className="text-muted-foreground">Department</p><p className="font-medium">{u.department || "—"}</p></div>
                <div><p className="text-muted-foreground">Location</p><p className="font-medium">{u.location || "—"}</p></div>
              </div>
              {u.role === "trainer" && u.qualifications?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {u.qualifications.map((q) => <span key={q} className="text-[11px] px-2 py-0.5 rounded-full bg-accent text-accent-foreground">{q}</span>)}
                </div>
              )}
              <div className="mt-3 flex gap-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => setResetUser(u)} data-testid={`reset-password-${u.id}-button`}><KeyRound className="h-4 w-4 mr-1.5" />Reset password</Button>
                {u.role !== "admin" && (
                  <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-600" onClick={() => remove(u.id)} data-testid={`delete-user-${u.id}-button`}><Trash2 className="h-4 w-4" /></Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <AddUserDialog open={open} setOpen={setOpen} onSaved={load} />
      {resetUser && <ResetPasswordDialog user={resetUser} onClose={() => setResetUser(null)} />}
    </div>
  );
}

function ResetPasswordDialog({ user, onClose }) {
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    setSaving(true);
    try {
      await api.put(`/users/${user.id}/password`, { password });
      toast.success(`Password updated for ${user.name}`);
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-heading">Reset password — {user.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Set a new password for <span className="font-mono">{user.email}</span>. They'll be signed out of existing sessions.</p>
          <div><Label>New password</Label><Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" data-testid="reset-password-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="reset-password-save-button">{saving ? "Saving..." : "Update password"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddUserDialog({ open, setOpen, onSaved }) {
  const empty = { name: "", email: "", password: "", role: "employee", department: "", location: "", job_title: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Name, email and password are required");
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      setForm(empty); setOpen(false); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">Add Person</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Full name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="user-name-input" /></div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["employee", "trainer", "admin"].map((r) => <SelectItem key={r} value={r} className="capitalize">{r === "employee" ? "learner" : r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" data-testid="user-email-input" /></div>
          <div><Label>Temporary password</Label><Input type="text" value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5" data-testid="user-password-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Department</Label><Input value={form.department} onChange={(e) => set("department", e.target.value)} className="mt-1.5" /></div>
            <div><Label>Location</Label><Input value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-1.5" /></div>
          </div>
          <div><Label>Job title</Label><Input value={form.job_title} onChange={(e) => set("job_title", e.target.value)} className="mt-1.5" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-user-button">{saving ? "Creating..." : "Create User"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
